/**
 * BehavioralDecayMonitoring — Admin → Intelligence
 * ------------------------------------------------------------------
 * Monitors lifecycle decay across hooks, archetypes, pacing, and
 * escalation paths. Surfaces declining structures, replay collapse,
 * archetype fatigue, and pacing burnout. Hooks into the deployment
 * decay scan to forecast retirement timing.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, AlertTriangle, ScanLine, Skull, TrendingDown, Flame } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  DEPLOYMENT_DECAY_EVENT,
  DEPLOYMENT_EVENT,
  runDecayScan,
  type DecaySignal,
} from "@/lib/deploymentEngine";

interface PlatformDriftRow {
  id: string;
  platform: string;
  behavioral_pattern: string;
  trend_direction: string;
  delta: number;
  signal_strength: number;
  detected_at: string;
}

const TREND_TONE: Record<string, string> = {
  saturating: "border-amber-400/50 text-amber-300 bg-amber-400/10",
  declining: "border-orange-500/50 text-orange-300 bg-orange-500/10",
  fatigue: "border-amber-400/50 text-amber-300 bg-amber-400/10",
  burnout: "border-red-500/50 text-red-400 bg-red-500/10",
};

const TREND_ICON: Record<string, any> = {
  saturating: Activity,
  declining: TrendingDown,
  fatigue: AlertTriangle,
  burnout: Skull,
};

export default function BehavioralDecayMonitoring() {
  const [signals, setSignals] = useState<DecaySignal[]>([]);
  const [drift, setDrift] = useState<PlatformDriftRow[]>([]);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState<string | null>(null);

  const loadDrift = async () => {
    const { data } = await (supabase as any)
      .from("behavioral_platform_drift")
      .select("*")
      .in("trend_direction", ["declining", "saturating", "fatigue", "burnout", "decreasing"])
      .order("detected_at", { ascending: false })
      .limit(12);
    setDrift((data ?? []) as PlatformDriftRow[]);
  };

  useEffect(() => {
    loadDrift();
    const onDecay = (e: any) => {
      const s = (e.detail?.signals ?? []) as DecaySignal[];
      if (s.length) setSignals(s);
      setLastScan(new Date().toLocaleTimeString());
    };
    const onChange = () => loadDrift();
    window.addEventListener(DEPLOYMENT_DECAY_EVENT, onDecay);
    window.addEventListener(DEPLOYMENT_EVENT, onChange);
    return () => {
      window.removeEventListener(DEPLOYMENT_DECAY_EVENT, onDecay);
      window.removeEventListener(DEPLOYMENT_EVENT, onChange);
    };
  }, []);

  const handleScan = async () => {
    setScanning(true);
    const s = await runDecayScan();
    setSignals(s);
    setLastScan(new Date().toLocaleTimeString());
    setScanning(false);
  };

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base">
            <Flame className="w-5 h-5 text-amber-400 animate-pulse" />
            Behavioral Decay Monitoring
            {lastScan && (
              <span className="text-[10px] text-muted-foreground font-mono ml-2">last scan {lastScan}</span>
            )}
          </CardTitle>
          <Button size="sm" variant="outline" disabled={scanning} onClick={handleScan}
            className="h-8 text-xs border-amber-400/40 text-amber-300 hover:bg-amber-400/10">
            <ScanLine className={`w-3 h-3 mr-1 ${scanning ? "animate-pulse" : ""}`} /> Run Decay Scan
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Live decay signals from last scan */}
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
            ▸ Live Decay Signals
          </div>
          {signals.length === 0 && (
            <div className="text-xs text-muted-foreground italic">
              No decay events captured yet. Run a decay scan to surface burnout, replay collapse, and archetype fatigue.
            </div>
          )}
          {signals.map((s, i) => {
            const Icon = TREND_ICON[s.trend] ?? AlertTriangle;
            return (
              <div key={i} className={`rounded border px-2.5 py-1.5 flex items-center gap-2 ${TREND_TONE[s.trend] ?? "border-glass-border"}`}>
                <Icon className="w-3.5 h-3.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono truncate">{s.note}</div>
                  <div className="text-[9px] uppercase tracking-wider opacity-70">
                    {s.scope} · {s.trend} · intensity {s.intensity}%
                  </div>
                </div>
                <Badge className="text-[9px] uppercase border-current bg-transparent" variant="outline">
                  {s.platform ?? "multi"}
                </Badge>
              </div>
            );
          })}
        </div>

        {/* Persistent platform drift log */}
        <div className="space-y-1.5 pt-2 border-t border-glass-border">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
            ▸ Persistent Platform Drift Log
          </div>
          {drift.length === 0 && (
            <div className="text-xs text-muted-foreground italic">No declining drift events recorded.</div>
          )}
          {drift.map((d) => (
            <div key={d.id} className="flex items-center justify-between text-[10px] font-mono py-1 border-b border-glass-border/40 last:border-0">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[9px] border-amber-400/40 text-amber-300">{d.platform}</Badge>
                <span className="text-muted-foreground">{d.behavioral_pattern}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-orange-300">Δ {Number(d.delta).toFixed(1)}</span>
                <span className="text-cyan">sig {Math.round(Number(d.signal_strength))}</span>
                <span className="text-muted-foreground">{new Date(d.detected_at).toLocaleTimeString()}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
